import { useForm, isEmail } from '@mantine/form'
import countries from 'i18n-iso-countries'
import enLang from 'i18n-iso-countries/langs/en.json'
import { useState } from 'react'

import domainsText from '~/assets/references/all_email_provider_domains.txt?raw'
import { TagManagerEventType } from '~/platform/utils/googleTagManager/tagManagerModels'
import { triggerTagManagerEvent } from '~/platform/utils/googleTagManager/tagManagerUtils'
import { TARIFF_CALC_STRINGS } from '~/widgets/TariffCalculator/strings'

const BUSINESS_EMAIL_ERROR = 'Please use a business email address'

const SELF_SERVE_REQUEST_URL =
  'https://service-run-api.muir.ai/public_reference/self_serve_request'
const MAKE_EMAIL_WEBHOOK_URL =
  'https://hook.us2.make.com/0ide8movtd9r38vey4yc2s5os7er8y5s'
// Register locale for country code lookups
countries.registerLocale(enLang)

interface EmailFormValues {
  firstName: string
  lastName: string
  email: string
}

interface UseEmailCaptureProps {
  product: string
  country: string | null
}

export function useEmailCapture({ product, country }: UseEmailCaptureProps) {
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false)
  const [emailSubmitted, setEmailSubmitted] = useState(false)

  const disallowedDomains = new Set(
    domainsText.split('\n').filter((domain) => domain.trim() !== ''),
  )

  const personalDomains = [
    'gmail.',
    'yahoo.',
    'aol.',
    'msn.',
    'hotmail.',
    'outlook.',
    'icloud.',
    'live.',
    'wanadoo.',
    'orange.',
    'comcast.',
    'rediffmail.',
    'free.',
    'uol.',
    'bol.',
    'sbcglobal.',
    'mail.',
    'verizon.',
    'terra.',
    'rocketmail.',
    'example.',
  ]

  const emailForm = useForm<EmailFormValues>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
    },
    validate: {
      firstName: () => null,
      lastName: () => null,
      email: (value) => {
        if (!value.trim()) return TARIFF_CALC_STRINGS.emailRequired
        const basicEmailValidation = isEmail('Invalid email format')(value)
        if (basicEmailValidation) {
          return basicEmailValidation
        }
        return null
      },
    },
  })

  const handleEmailSubmit = async (values: EmailFormValues) => {
    // Enforce company email on submit (not during typing)
    const domain = values.email.split('@')[1]?.toLowerCase()
    const isPersonalDomain =
      personalDomains.some((personalDomain) =>
        domain?.startsWith(personalDomain),
      ) ||
      disallowedDomains.has(domain) ||
      domain?.includes('.edu')

    if (isPersonalDomain) {
      emailForm.setFieldError('email', BUSINESS_EMAIL_ERROR)
      return
    }

    setIsSubmittingEmail(true)

    try {
      // Build payloads
      const countryCode = country
        ? countries.getAlpha2Code(country, 'en') || ''
        : ''

      const internalRequest = fetch(SELF_SERVE_REQUEST_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_name: product,
          country_code: countryCode,
          email: values.email,
        }),
      })

      const makeWebhookRequest = fetch(MAKE_EMAIL_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          product,
          country: country || '',
        }),
      })

      // Fire both requests in parallel
      const [internalResult, makeResult] = await Promise.allSettled([
        internalRequest,
        makeWebhookRequest,
      ])

      const internalOk =
        internalResult.status === 'fulfilled' && internalResult.value.ok
      const makeOk = makeResult.status === 'fulfilled' && makeResult.value.ok

      // Consider success if at least one succeeded
      if (internalOk || makeOk) {
        triggerTagManagerEvent(TagManagerEventType.tariffEmailSubmitted)

        // Show success state
        setEmailSubmitted(true)
        emailForm.reset()
      } else {
        throw new Error('Failed to submit email')
      }
    } finally {
      setIsSubmittingEmail(false)
    }
  }

  const handleDemoClick = () => {
    triggerTagManagerEvent(TagManagerEventType.tariffMeetingBooked)
  }

  return {
    emailForm,
    isSubmittingEmail,
    emailSubmitted,
    handleEmailSubmit,
    handleDemoClick,
  }
}
